import React, { FC, SetStateAction } from "react";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import axios from "axios";
import { DictType, GeometryFeature } from "@pma/types";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { toast } from "@hooks/use-toast";
import useTranslator from "@hooks/use-translator";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card";
import { Input } from "@components/common/input";
import { Separator } from "@components/common/separator";
import { Button } from "@components/common/button";
import { parseCookies } from "nookies";
import {
  PROJECT_MAP_ADD_ANNOTATIONS_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import { useMutation } from "@tanstack/react-query";

interface Props {
  annotation: GeometryFeature;
  projectId: number;
  setAnnotations: React.Dispatch<SetStateAction<any | null>>;
  setShowAnnotationPanel: React.Dispatch<SetStateAction<boolean>>;
  mapRef: any;
  textAnnotations?: string | null | undefined;
  refetch: CallableFunction;
}

const AddAnnotation: FC<Props> = ({
  annotation,
  projectId,
  setAnnotations,
  mapRef,
  setShowAnnotationPanel,
  textAnnotations,
  refetch,
}: Props) => {
  const { translate, locale } = useTranslator();

  const [annotationText, setAnnotationText] = React.useState(
    textAnnotations ? textAnnotations : "",
  );

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const canSave = annotationText !== "";

  const getDrawControl = (map: any) => {
    let drawControl;
    if (map && map._controls) {
      for (const control of map._controls) {
        if (control instanceof MapboxDraw) {
          drawControl = control;
          break;
        }
      }
    }
    return drawControl;
  };

  const addAnnotation = async () => {
    const response = await axios.post(
      SERVER_URL + PROJECT_MAP_ADD_ANNOTATIONS_ENDPOINT,
      {
        annotation: annotationText,
        project_id: projectId,
        lon: annotation.geometry.coordinates?.at(0),
        lat: annotation.geometry.coordinates?.at(1),
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addAnnotationMutation = useMutation({
    mutationKey: ["add-annotation"],
    mutationFn: addAnnotation,
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Annotation Added"),
        variant: "success",
      });

      const map = mapRef.current?.getMap();

      const drawControl = getDrawControl(map);
      drawControl?.delete(annotation.id);

      setAnnotations(null);
      setShowAnnotationPanel(false);
      refetch();
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("An error occurred"),
        variant: "error",
      });
    },
  });

  return (
    <Card className="bg-white shadow-none border-none w-full">
      <CardHeader className="p-0">
        <CardTitle>{translate("Add annotation")}</CardTitle>{" "}
      </CardHeader>
      <Separator className="my-2" />
      <CardContent className="px-0">
        <Input
          value={annotationText}
          onChange={(e) => setAnnotationText(e.target.value)}
          placeholder={translate("Annotation")}
          className="w-full"
          required
        />
      </CardContent>
      <CardFooter className="flex justify-end my-0 p-0">
        <Button
          size={"sm"}
          disabled={!canSave || addAnnotationMutation.isPending}
          onClick={() => addAnnotationMutation.mutate()}
        >
          {translate("Save")}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AddAnnotation;
